import type { FileManagerRevealKind } from "@cmmd-forge/contracts";
import type { ComponentProps } from "react";

import { openInPreferredEditor } from "../../editorPreferences";
import { readEnvironmentApi } from "../../environmentApi";
import { readLocalApi } from "../../localApi";
import { resolveMarkdownFileLinkMeta, type MarkdownFileLinkMeta } from "../../markdown-links";
import { useRightPanelStore } from "../../rightPanelStore";
import { toastManager } from "../ui/toast";
import { MarkdownFileLink } from "./MarkdownFileLink";

export function openMarkdownFileLink(input: {
  readonly meta: MarkdownFileLinkMeta;
  readonly threadKey: string | undefined;
  readonly executionApi: ReturnType<typeof readEnvironmentApi>;
}) {
  if (input.threadKey && input.meta.workspaceRelativePath) {
    useRightPanelStore
      .getState()
      .openFile(input.threadKey, input.meta.workspaceRelativePath, input.meta.line ?? null);
    return;
  }
  if (input.executionApi !== undefined) {
    void input.executionApi.shell
      .openInEditor({ cwd: input.meta.filePath, editor: "file-manager", reveal: true })
      .catch((cause: unknown) => {
        toastManager.add({
          type: "error",
          title: "Unable to reveal file",
          description: cause instanceof Error ? cause.message : "An error occurred.",
        });
      });
    return;
  }
  const api = readLocalApi();
  if (api) {
    void openInPreferredEditor(api, input.meta.targetPath);
  } else {
    console.warn("Native API not found. Unable to open file in editor.");
  }
}

export function ExplicitMarkdownFileLink(props: {
  readonly href: string | undefined;
  readonly cwd: string | undefined;
  readonly anchorProps: Omit<ComponentProps<"a">, "href">;
  readonly copyMarkdown: string;
  readonly theme: "light" | "dark";
  readonly onOpen: (meta: MarkdownFileLinkMeta) => void;
  readonly revealKind: FileManagerRevealKind | undefined;
}) {
  const meta = resolveMarkdownFileLinkMeta(props.href, props.cwd);
  return meta === null ? (
    <a {...props.anchorProps} href={props.href} />
  ) : (
    <MarkdownFileLink
      meta={meta}
      copyMarkdown={props.copyMarkdown}
      theme={props.theme}
      onOpen={props.onOpen}
      revealKind={props.revealKind}
    />
  );
}
